import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Textarea,
  IconButton,
  Button,
  useToast,
  Input,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
  IconProps,
  Collapse,
  Skeleton,
  SkeletonText,
  Spinner,
  SimpleGrid,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  SkeletonCircle,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from "@chakra-ui/react";
import {
  AttachmentIcon,
  ChevronDownIcon,
  CloseIcon,
  ArrowUpIcon,
  ChatIcon,
  LockIcon,
  EditIcon,
  ArrowDownIcon,
  RepeatIcon,
  TimeIcon,
} from "@chakra-ui/icons";
import { GoogleGenerativeAI } from "@google/generative-ai";
import axios, { AxiosRequestConfig, AxiosError } from "axios";
import {
  useCreateExperience,
  useGetForumPosts,
  useGetForumPostById,
  useSummarizeTextWithGemini as useSummarizeText,
  TCreateExperiencePayload,
  ForumPost,
} from "../apis/experiences";
import { useAppSelector } from "../hooks/useAppSelector";
import { selectCurrentDoctorInfo } from "../store/authSlice";
import { debounce } from "../utils/debounce";
import { FireIcon } from "../components/icons/FireIcon";
import PostCard from "../components/PostCard";
import { useLocation, useNavigate } from "react-router-dom";

interface MediaContent {
  url: string;
  type: "image" | "video";
}

const PlayIcon = (props: IconProps) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M8 5v14l11-7z" />
  </Icon>
);

const PauseIcon = (props: IconProps) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </Icon>
);

const MAX_FILES = 3;
const MIN_WORDS_FOR_SUMMARY = 7;
const CHARS_PER_CHUNK = 3;
const TYPEWRITER_CHUNK_SPEED = 50;

const MODEL_NAME = "gemini-2.5-flash";
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

async function generate(promptText: string) {
  if (!API_KEY) {
    throw new Error("Missing Gemini API key.");
  }
  const genAI = new GoogleGenerativeAI(API_KEY);
  const model = genAI.getGenerativeModel({ model: MODEL_NAME });

  const result = await model.generateContent(promptText);
  const response = result.response;
  const text = response.text();
  try {
    const jsonString = text.replace(/```json\n?|\n?```/g, "").trim();
    const parsedResponse = JSON.parse(jsonString);
    if (parsedResponse.title && parsedResponse.reason) {
      return parsedResponse;
    } else {
      return { title: "", reason: "" };
    }
  } catch (error) {
    console.error("Error parsing Gemini response:", error);
    return { title: "", reason: "" };
  }
}
const makeClientRequestId = () =>
  (crypto as any)?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// dedupe helper: keeps only one item per _id
const dedupeById = (arr: ForumPost[] = []) => {
  const map = new Map<string, ForumPost>();
  for (const item of arr) {
    if (item && item._id && !map.has(item._id)) {
      map.set(item._id, item);
    }
  }
  return Array.from(map.values());
};

const Meet: React.FC = () => {
  const [postTitle, setPostTitle] = useState("");
  const [reason, setReason] = useState("");
  const [newPostText, setNewPostText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [showTagsInput, setShowTagsInput] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<
    "idle" | "reviewing" | "pending" | "submitted"
  >("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newlySubmittedPostId, setNewlySubmittedPostId] = useState<
    string | null
  >(null);

  const {
    data: newlyFetchedPost,
    isLoading: isLoadingNewlyFetchedPost,
    isError: isErrorNewlyFetchedPost,
    refetch: refetchNewlyFetchedPost,
  } = useGetForumPostById(newlySubmittedPostId);

  const createExperienceMutation = useCreateExperience();
  const summarizeTextMutation = useSummarizeText();

  const [currentSort, setCurrentSort] = useState<
    "latest" | "popular" | "friends"
  >("popular");
  const {
    data: forumPosts,
    isLoading: isLoadingPosts,
    isError: isErrorPosts,
    error: postsError,
    refetch: refetchForumPosts,
  } = useGetForumPosts(currentSort === "friends" ? "latest" : currentSort);

  const [modalContent, setModalContent] = useState<MediaContent | null>(null);
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const doctorInfo = useAppSelector(selectCurrentDoctorInfo);
  const navigate = useNavigate();

  const [brokenImageUrls, setBrokenImageUrls] = useState<Set<string>>(
    new Set()
  );
  const [isPreparingReview, setIsPreparingReview] = useState(false);
  const [isFilling, setIsFilling] = useState(false);
  const [fillPercentage, setFillPercentage] = useState(0);
  const [remainingTime, setRemainingTime] = useState(20);
  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const [aiResultDisplayed, setAiResultDisplayed] = useState(false);
  const [isGeneratingGeminiTitle, setIsGeneratingGeminiTitle] = useState(false);
  const [displayedTitle, setDisplayedTitle] = useState("");
  const [displayedReason, setDisplayedReason] = useState("");
  const [typingStage, setTypingStage] = useState<
    "idle" | "title" | "reason" | "finished"
  >("idle");
  const typewriterIntervalRef = useRef<number | null>(null);
  const [isEditingTitleManually, setIsEditingTitleManually] = useState(false);

  const newlySubmittedPostRef = useRef<HTMLDivElement>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [previousScrollPosition, setPreviousScrollPosition] = useState<
    number | null
  >(null);
  const [showScrollActionButtons, setShowScrollActionButtons] = useState(false);
  const location = useLocation();

  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  const [titleHistory, setTitleHistory] = useState<string[]>([]);
  const [titleHistoryIndex, setTitleHistoryIndex] = useState(-1);
  const [isProgrammaticTitleSet, setIsProgrammaticTitleSet] = useState(false);

  const debouncedSummarize = useCallback(
    debounce((text: string) => {
      if (text.trim().split(/\s+/).length >= MIN_WORDS_FOR_SUMMARY) {
        summarizeTextMutation
          .mutateAsync({ textToSummarize: text })
          .then((result) => {
            if (result?.summary) {
              setPostTitle(result.summary);
              setReason("");
              setDisplayedTitle(result.summary);
              setDisplayedReason("");
              setTypingStage("finished");
            }
          })
          .catch((apiError) => {
            console.error("Error summarizing text:", apiError);
          });
      }
    }, 500),
    [summarizeTextMutation]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      const newFiles = filesArray.slice(0, MAX_FILES - selectedFiles.length);

      if (newFiles.length === 0 && filesArray.length > 0) {
        toast({
          title: "File limit reached",
          description: `You can only attach up to ${MAX_FILES} files. Some files were not added.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }

      setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
      const newPreviewUrls = newFiles.map((file) => URL.createObjectURL(file));
      setPreviewUrls((prevUrls) => [...prevUrls, ...newPreviewUrls]);

      if (newFiles.length > 0 && !showTagsInput) {
        setShowTagsInput(true);
      }
    }
  };

  const handlePreviewClick = (url: string, type: "image" | "video") => {
    setModalContent({ url, type });
    onModalOpen();
  };

  const handlePreviewImageError = (url: string) => {
    setBrokenImageUrls((prev) => new Set(prev).add(url));
    toast({
      title: "Preview Error",
      description: "Could not load preview for one of the images.",
      status: "warning",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  };

  useEffect(() => {
    const urlsToRevoke = [...previewUrls];
    return () => {
      urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleAttachmentClick = () => {
    if (selectedFiles.length < MAX_FILES) {
      fileInputRef.current?.click();
    } else {
      toast({
        title: "File limit reached",
        description: `You can only attach up to ${MAX_FILES} files.`,
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleNewPostTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const text = e.target.value;
    setNewPostText(text);

    if (showScrollActionButtons) {
      setShowScrollActionButtons(false);
      setPreviousScrollPosition(null);
    }

    if (submissionStatus === "reviewing" || submissionStatus === "submitted") {
      setSubmissionStatus("idle");
      setIsFilling(false);
      setFillPercentage(0);
      setRemainingTime(20);
      setAiResultDisplayed(false);
    }

    if (text.trim() !== "" || selectedFiles.length > 0) {
      setShowTagsInput(true);
      if (
        submissionStatus !== "submitted" &&
        submissionStatus !== "reviewing" &&
        !isGeneratingGeminiTitle
      ) {
        debouncedSummarize(text);
      }
    } else {
      setShowTagsInput(false);
      setPostTitle("");
      setReason("");
      setDisplayedTitle("");
      setDisplayedReason("");
      setSubmissionStatus("idle");
      setIsFilling(false);
    }
  };

  const handleRemoveSingleFile = (indexToRemove: number) => {
    if (previewUrls[indexToRemove]) {
      URL.revokeObjectURL(previewUrls[indexToRemove]);
    }
    const newSelectedFiles = selectedFiles.filter(
      (_, index) => index !== indexToRemove
    );
    const newPreviewUrls = previewUrls.filter(
      (_, index) => index !== indexToRemove
    );

    setSelectedFiles(newSelectedFiles);
    setPreviewUrls(newPreviewUrls);

    if (newSelectedFiles.length === 0 && newPostText.trim() === "") {
      setShowTagsInput(false);
    }
    if (newSelectedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetTimer = useCallback(() => {
    setIsFilling(false);
    setFillPercentage(0);
    setRemainingTime(20);
    setIsTimerPaused(false);
  }, [setIsFilling, setFillPercentage, setRemainingTime, setIsTimerPaused]);

  const handleCollapseInput = () => {
    setShowTagsInput(false);
    setSubmissionStatus("idle");
    resetTimer();
    setAiResultDisplayed(false);
    setIsGeneratingGeminiTitle(false);
  };

  const handleReturnToEdit = () => {
    setIsEditingTitleManually(false);
    setSubmissionStatus("idle");
    setShowTagsInput(true);
    resetTimer();
    setAiResultDisplayed(false);
    setIsGeneratingGeminiTitle(false);
    setReason("");
    setDisplayedTitle(postTitle);
    setDisplayedReason("");
    setTypingStage("idle");
  };

  useEffect(() => {
    const apiUrl = `${process.env.REACT_APP_BASE_URL || ""}/forum/posts/tags`;
    const token = localStorage.getItem("authToken");
    const config: AxiosRequestConfig = {};
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` };
    }
    axios
      .get(apiUrl, config)
      .then((res) => {
        const fetchedTags = res.data?.data?.tags;
        if (Array.isArray(fetchedTags)) {
          setAllTags(
            fetchedTags.filter(
              (tag) =>
                typeof tag === "string" &&
                tag.trim() !== "" &&
                !tag.includes(" ")
            )
          );
        } else {
          setAllTags([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching tags:", err);
        setAllTags([]);
      });
  }, []);

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue.endsWith(",")) {
      const potentialTag = inputValue
        .slice(0, -1)
        .replace(/\s/g, "")
        .toLowerCase();
      if (potentialTag && !selectedTags.includes(potentialTag)) {
        setSelectedTags((prevTags) => [...prevTags, potentialTag]);
      }
      setTagInput("");
      setTagSuggestions([]);
      return;
    }
    const processedInputValue = inputValue.replace(/\s/g, "");
    setTagInput(processedInputValue);
    if (processedInputValue.trim().length > 0) {
      const suggestions = allTags
        .filter(
          (tag) =>
            tag
              .toLowerCase()
              .startsWith(processedInputValue.trim().toLowerCase()) &&
            !selectedTags.includes(tag.toLowerCase())
        )
        .slice(0, 5);
      setTagSuggestions(suggestions);
    } else {
      setTagSuggestions([]);
    }
  };

  const handleTagSuggestionClick = (tag: string) => {
    const lowerTag = tag.toLowerCase();
    if (!selectedTags.includes(lowerTag)) {
      setSelectedTags([...selectedTags, lowerTag]);
    }
    setTagInput("");
    setTagSuggestions([]);
    resetTimer();
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
    resetTimer();
  };

  useEffect(() => {
    if (isProgrammaticTitleSet) {
      setIsProgrammaticTitleSet(false);
      return;
    }
    if (postTitle && postTitle.trim() !== "") {
      if (
        titleHistoryIndex >= 0 &&
        titleHistoryIndex < titleHistory.length &&
        titleHistory[titleHistoryIndex] === postTitle
      ) {
        return;
      }
      let currentHistory = [...titleHistory];
      let currentIndex = titleHistoryIndex;
      if (currentIndex >= -1 && currentIndex < currentHistory.length - 1) {
        currentHistory = currentHistory.slice(0, currentIndex + 1);
      }
      if (
        currentHistory.length > 0 &&
        currentHistory[currentHistory.length - 1] === postTitle
      ) {
        if (currentIndex !== currentHistory.length - 1) {
          setTitleHistoryIndex(currentHistory.length - 1);
        }
        if (
          titleHistory.length !== currentHistory.length ||
          !titleHistory.every((val, idx) => val === currentHistory[idx])
        ) {
          setTitleHistory(currentHistory);
        }
        return;
      }
      const newHistory = [...currentHistory, postTitle];
      setTitleHistory(newHistory);
      setTitleHistoryIndex(newHistory.length - 1);
    }
  }, [postTitle, titleHistory, titleHistoryIndex, isProgrammaticTitleSet]);

  const handlePrevTitle = () => {
    if (titleHistoryIndex > 0) {
      const newIndex = titleHistoryIndex - 1;
      setTitleHistoryIndex(newIndex);
      setIsProgrammaticTitleSet(true);
      const newTitle = titleHistory[newIndex];
      setPostTitle(newTitle);
      setReason("");
      setDisplayedTitle(newTitle);
      setDisplayedReason("");
      setTypingStage("finished");
      resetTimer();
      if (submissionStatus === "reviewing" && aiResultDisplayed) {
        setIsFilling(true);
      }
    }
  };

  const handleNextTitle = () => {
    if (titleHistoryIndex < titleHistory.length - 1) {
      const newIndex = titleHistoryIndex + 1;
      setTitleHistoryIndex(newIndex);
      setIsProgrammaticTitleSet(true);
      const newTitle = titleHistory[newIndex];
      setPostTitle(newTitle);
      setReason("");
      setDisplayedTitle(newTitle);
      setDisplayedReason("");
      setTypingStage("finished");
      resetTimer();
      if (submissionStatus === "reviewing" && aiResultDisplayed) {
        setIsFilling(true);
      }
    }
  };

  const handleChangeTitle = async (): Promise<boolean> => {
    setIsEditingTitleManually(false);
    resetTimer();
    setIsGeneratingGeminiTitle(true);
    setTypingStage("idle");
    setDisplayedTitle("");
    setDisplayedReason("");
    setReason("");
    try {
      const geminiResponse = await generate(
        JSON.stringify({
          persona:
            "You're a creative content strategist for a social platform where doctors connect and share stories.",
          context:
            "The audience is doctors talking to other doctors. The vibe is relaxed, like a lounge or a coffee break, not a lecture hall. They're here to share real-life experiences, funny moments, and interesting cases in a casual way.",
          task: "Analyze the following post content from a doctor and create a catchy, summary-style title for it.",
          instructions: {
            title:
              "The title must be directly based on the key information, story, or question in the `postContent`. It should summarize the core idea in an intriguing, relatable, and casual way. It should sound like something one doctor would say to another to get them to listen to a story. Do not generate a random or generic title.",
            reason:
              "In one simple sentence, explain how your title captures the essence of the post while being engaging for other doctors.",
          },
          postContent: newPostText,
          responseFormat: {
            title: "The catchy, summary-style title",
            reason: "Your simple explanation.",
          },
        })
      );
      setPostTitle(geminiResponse.title);
      setReason(geminiResponse.reason);
      setAiResultDisplayed(true);
      setTypingStage("title");
      return true;
    } catch (apiError) {
      toast({
        title: "Error generating title",
        description: "Failed to generate title using Gemini AI.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      console.error("Gemini title generation error:", apiError);
      setTypingStage("idle");
      setIsFilling(false);
      return false;
    } finally {
      setIsGeneratingGeminiTitle(false);
    }
  };

  const handlePreparePostForReview = async () => {
    if (!doctorInfo?._id) {
      toast({
        title: "Authentication Error",
        description: "Doctor information not found. Please log in again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    if (!newPostText.trim() && selectedFiles.length === 0) {
      toast({
        title: "Cannot proceed with an empty post.",
        description: "Please add some text or an attachment.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    if (selectedTags.length === 0) {
      toast({
        title: "Tags are required",
        description: "Please add at least one tag.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    setIsPreparingReview(true);
    try {
      setSubmissionStatus("reviewing");
      setShowTagsInput(true);
      const titleGeneratedSuccessfully = await handleChangeTitle();

      if (titleGeneratedSuccessfully) {
        setIsFilling(true);
      } else {
        setIsFilling(false);
      }
    } catch (apiError) {
      toast({
        title: "Preparation Error",
        description: "Could not prepare the post for review.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error preparing post for review:", apiError);
      setSubmissionStatus("idle");
      setIsFilling(false);
    } finally {
      setIsPreparingReview(false);
    }
  };

  const handleConfirmAndSubmitPost = useCallback(async () => {
    if (createExperienceMutation.isPending) {
      return;
    }
    if (
      !doctorInfo?._id ||
      !postTitle.trim() ||
      (!newPostText.trim() && selectedFiles.length === 0) ||
      selectedTags.length === 0
    ) {
      toast({
        title: "Submission Error",
        description:
          "Some required information is missing or invalid. Please review your post.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      setSubmissionStatus("idle");
      resetTimer();
      return;
    }

    const experiencePayload: TCreateExperiencePayload = {
      title: postTitle,
      content: newPostText,
      tags: selectedTags.join(","),
    };

    if (scrollableContainerRef.current) {
      setPreviousScrollPosition(scrollableContainerRef.current.scrollTop);
    }

    setSubmissionStatus("pending");
    setUploadProgress(0);
    setNewlySubmittedPostId(null);

    try {
      const createdPostResponse = await createExperienceMutation.mutateAsync({
        payload: experiencePayload,
        files: selectedFiles,
        onProgressUpdate: (progress) => {
          setUploadProgress(progress);
        },
      });

      toast({
        title: "Post Submitted!",
        description: "Your experience has been shared successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      if (createdPostResponse && createdPostResponse._id) {
        setNewlySubmittedPostId(createdPostResponse._id);
      }
      setPostTitle("");
      setReason("");
      setNewPostText("");
      setSelectedTags([]);
      setTagInput("");
      setTagSuggestions([]);
      setSelectedFiles([]);
      setPreviewUrls((prevUrls) => {
        prevUrls.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });
      setUploadProgress(0);
      setSubmissionStatus("submitted");
      setShowTagsInput(false);
      resetTimer();
    } catch (error: any) {
      let toastTitle = "Submission Failed";
      let toastDescription =
        "Could not submit your experience. Please try again.";

      if (error?.response?.data?.message) {
        toastDescription = error.response.data.message;
      } else if (
        axios.isAxiosError(error) &&
        (error.code === "ECONNABORTED" || error.message.includes("timeout"))
      ) {
        toastTitle = "Submission Timeout";
        toastDescription =
          "The submission timed out. This can happen with large files or a slow connection. Please check and try again.";
      } else if (error?.message) {
        toastDescription = error.message;
      }

      console.error("Submission error:", error);
      toast({
        title: toastTitle,
        description: toastDescription,
        status: "error",
        duration: 7000,
        isClosable: true,
        position: "top-right",
      });
      setUploadProgress(0);
      setSubmissionStatus("reviewing");
      setIsFilling(false);
      setIsTimerPaused(true);
      setFillPercentage(0);
      setRemainingTime(20);
    }
  }, [
    doctorInfo?._id,
    postTitle,
    newPostText,
    selectedFiles,
    selectedTags,
    createExperienceMutation,
    toast,
    resetTimer,
  ]);

  useEffect(() => {
    if (newlyFetchedPost && newlySubmittedPostRef.current) {
      newlySubmittedPostRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      if (previousScrollPosition !== null) {
        setShowScrollActionButtons(true);
      }
    }
  }, [newlyFetchedPost, previousScrollPosition]);

  useEffect(() => {
    const mainScrollableBox = scrollableContainerRef.current;
    let scrollTimeout: NodeJS.Timeout | null = null;
    const handleManualScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (showScrollActionButtons) {
          setShowScrollActionButtons(false);
        }
      }, 150);
    };
    if (mainScrollableBox && showScrollActionButtons) {
      mainScrollableBox.addEventListener("scroll", handleManualScroll);
    }
    return () => {
      if (mainScrollableBox) {
        mainScrollableBox.removeEventListener("scroll", handleManualScroll);
      }
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [showScrollActionButtons]);

  const handleScrollBackDown = () => {
    if (scrollableContainerRef.current && previousScrollPosition !== null) {
      scrollableContainerRef.current.scrollTo({
        top: previousScrollPosition,
        behavior: "smooth",
      });
    }
    setShowScrollActionButtons(false);
    setPreviousScrollPosition(null);
  };

  const handleRefreshPosts = () => {
    refetchForumPosts();
    setShowScrollActionButtons(false);
    setNewlySubmittedPostId(null);
    setPreviousScrollPosition(null);
  };

  const handleToggleMessages = () => {};
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      isFilling &&
      fillPercentage < 100 &&
      aiResultDisplayed &&
      !isTimerPaused
    ) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
      intervalRef.current = window.setInterval(() => {
        setFillPercentage((prevPercentage) =>
          Math.min(prevPercentage + 100 / 20, 100)
        );
        setRemainingTime((prevTime) => Math.max(prevTime - 1, 0));
      }, 1000);
    } else {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isFilling, fillPercentage, aiResultDisplayed, isTimerPaused]);

  useEffect(() => {
    if (
      fillPercentage >= 100 &&
      submissionStatus === "reviewing" &&
      !createExperienceMutation.isPending
    ) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      handleConfirmAndSubmitPost();
    }
  }, [
    fillPercentage,
    handleConfirmAndSubmitPost,
    submissionStatus,
    createExperienceMutation.isPending,
  ]);

  useEffect(() => {
    const clearTypewriterInterval = () => {
      if (typewriterIntervalRef.current !== null) {
        clearInterval(typewriterIntervalRef.current);
        typewriterIntervalRef.current = null;
      }
    };
    if (typingStage === "title" && postTitle) {
      clearTypewriterInterval();
      let currentDisplay = "";
      setDisplayedTitle("");
      typewriterIntervalRef.current = window.setInterval(() => {
        if (currentDisplay.length < postTitle.length) {
          const remainingLength = postTitle.length - currentDisplay.length;
          const nextChunk = postTitle.substring(
            currentDisplay.length,
            currentDisplay.length + Math.min(CHARS_PER_CHUNK, remainingLength)
          );
          currentDisplay += nextChunk;
          setDisplayedTitle(currentDisplay);
        } else {
          clearTypewriterInterval();
          setTypingStage("reason");
        }
      }, TYPEWRITER_CHUNK_SPEED);
    } else if (typingStage === "reason" && reason) {
      clearTypewriterInterval();
      let currentDisplay = "";
      setDisplayedReason("");
      typewriterIntervalRef.current = window.setInterval(() => {
        if (currentDisplay.length < reason.length) {
          const remainingLength = reason.length - currentDisplay.length;
          const nextChunk = reason.substring(
            currentDisplay.length,
            currentDisplay.length + Math.min(CHARS_PER_CHUNK, remainingLength)
          );
          currentDisplay += nextChunk;
          setDisplayedReason(currentDisplay);
        } else {
          clearTypewriterInterval();
          setTypingStage("finished");
        }
      }, TYPEWRITER_CHUNK_SPEED);
    } else if (typingStage === "idle" || typingStage === "finished") {
      clearTypewriterInterval();
      if (postTitle && displayedTitle !== postTitle)
        setDisplayedTitle(postTitle);
      if (reason && displayedReason !== reason) setDisplayedReason(reason);
    }
    return clearTypewriterInterval;
  }, [typingStage, postTitle, reason]);

  const handlePrepareToNavigateToDetail = useCallback(
    (postId: string, hash?: string) => {
      if (scrollableContainerRef.current) {
        sessionStorage.setItem(
          "meetPageScrollPos",
          scrollableContainerRef.current.scrollTop.toString()
        );
      }

      // const postToNavigate = forumPosts?.find((p) => p._id === postId);

      const postToNavigate = dedupedPosts?.find((p) => p._id === postId);

      if (postToNavigate?.author?._id && postToNavigate?._id) {
        const path = `/meet/${postToNavigate.author._id}/posts/${
          postToNavigate._id
        }${hash || ""}`;
        navigate(path);
      } else {
        console.warn(
          `[Meet.tsx] Could not navigate to post ${postId}. Author or post details missing.`
        );
      }
    },
    [navigate, forumPosts, scrollableContainerRef]
  );

  useEffect(() => {
    const cameFromPostDetail =
      sessionStorage.getItem("meetPageScrollPos") !== null;
    if (cameFromPostDetail && scrollableContainerRef.current) {
      const savedScrollPos = sessionStorage.getItem("meetPageScrollPos");
      if (savedScrollPos) {
        setTimeout(() => {
          if (scrollableContainerRef.current) {
            scrollableContainerRef.current.scrollTop = parseInt(
              savedScrollPos,
              10
            );
          }
        }, 100);
        sessionStorage.removeItem("meetPageScrollPos");
      }
    }
  }, [location.key]);

  // 🔑 dedupe & hide the “newly submitted” post from the main list
  const dedupedPosts = useMemo(
    () => dedupeById(forumPosts || []),
    [forumPosts]
  );
  const visiblePosts = useMemo(() => {
    if (!newlySubmittedPostId) return dedupedPosts;
    return dedupedPosts.filter((p) => p._id !== newlySubmittedPostId);
  }, [dedupedPosts, newlySubmittedPostId]);

  return (
    <>
      <Box position="relative" w="full">
        <Box
          ref={scrollableContainerRef}
          w="full"
          pt={{ base: "15px", md: "30px" }}
          px={{ base: "15px", md: "30px" }}
          pb={`calc(${
            showTagsInput
              ? previewUrls.length > 0
                ? "380px"
                : "310px"
              : "70px"
          } + 64px + 30px)`}
          transition="filter 0.3s ease-in-out, background-color 0.3s ease-in-out"
          filter={showTagsInput ? "blur(3px)" : "none"}
          bg={showTagsInput ? "rgba(0, 0, 0, 0.3)" : "gray.100"}
          zIndex={1}
        >
          <VStack spacing={6} maxW="container.lg" mx="auto" align="stretch">
            <Flex
              direction={{ base: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ base: "flex-start", sm: "center" }}
              py={4}
              w="full"
            >
              <VStack align="flex-start" spacing={1}>
                <Heading size="md">Share your medical Experience</Heading>
                <Text color="gray.600" fontStyle="italic" fontSize="sm">
                  Meet new patients
                </Text>
              </VStack>
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  variant="outline"
                  borderColor="gray.300"
                  bg="white"
                  color="gray.700"
                  _hover={{ bg: "gray.50" }}
                  borderRadius="xl"
                  size="sm"
                  mt={{ base: 3, sm: 0 }}
                >
                  {currentSort === "popular"
                    ? "Popular"
                    : currentSort === "latest"
                    ? "Latest"
                    : "From Friends"}
                </MenuButton>
                <MenuList borderRadius="xl" boxShadow="lg">
                  <MenuItem
                    icon={<FireIcon color="orange.400" />}
                    onClick={() => setCurrentSort("popular")}
                    fontWeight={currentSort === "popular" ? "bold" : "normal"}
                    borderRadius="md"
                    mx={1}
                    my={0.5}
                    _hover={{ bg: "gray.100" }}
                  >
                    Popular
                  </MenuItem>
                  <MenuItem
                    icon={<TimeIcon color="purple.400" />}
                    onClick={() => setCurrentSort("latest")}
                    fontWeight={currentSort === "latest" ? "bold" : "normal"}
                    borderRadius="md"
                    mx={1}
                    my={0.5}
                    _hover={{ bg: "gray.100" }}
                  >
                    Latest
                  </MenuItem>
                  <MenuItem
                    isDisabled
                    icon={<LockIcon color="gray.400" />}
                    fontWeight={currentSort === "friends" ? "bold" : "normal"}
                    borderRadius="md"
                    mx={1}
                    my={0.5}
                    _hover={{ bg: "gray.100" }}
                  >
                    From Friends
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>

            {newlySubmittedPostId && (
              <Box ref={newlySubmittedPostRef} w="100%" my={4}>
                {isLoadingNewlyFetchedPost && (
                  <Box
                    padding="6"
                    boxShadow="lg"
                    bg="white"
                    w="100%"
                    borderRadius="xl"
                  >
                    <SkeletonCircle size="10" mb="4" />
                    <SkeletonText
                      mt="2"
                      noOfLines={1}
                      spacing="4"
                      skeletonHeight="6"
                      width="40%"
                    />
                    <SkeletonText
                      mt="4"
                      noOfLines={3}
                      spacing="4"
                      skeletonHeight="3"
                    />
                    <HStack mt="4" spacing="4">
                      <Skeleton height="20px" width="80px" borderRadius="md" />
                      <Skeleton height="20px" width="80px" borderRadius="md" />
                    </HStack>
                  </Box>
                )}
                {isErrorNewlyFetchedPost && (
                  <Alert status="error" borderRadius="xl">
                    <AlertIcon />
                    <Text>
                      Could not load your new post. It will appear in the list
                      below once available.
                    </Text>
                  </Alert>
                )}
                {newlyFetchedPost &&
                  !isLoadingNewlyFetchedPost &&
                  !isErrorNewlyFetchedPost && (
                    <>
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                        mb={2}
                        color="blue.600"
                        textAlign="center"
                      >
                        ✨ Your New Post ✨
                      </Text>
                      <PostCard
                        post={newlyFetchedPost}
                        currentDoctorId={doctorInfo?._id}
                        onNavigateToDetail={handlePrepareToNavigateToDetail}
                      />
                      <Divider my={6} />
                    </>
                  )}
              </Box>
            )}

            {isLoadingPosts && (
              <VStack spacing={4} mt={0}>
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    padding="6"
                    boxShadow="lg"
                    bg="white"
                    w="100%"
                    borderRadius="xl"
                  >
                    <SkeletonCircle size="10" mb="4" />
                    <SkeletonText
                      mt="2"
                      noOfLines={1}
                      spacing="4"
                      skeletonHeight="6"
                      width="40%"
                    />
                    <SkeletonText
                      mt="4"
                      noOfLines={3}
                      spacing="4"
                      skeletonHeight="3"
                    />
                    <HStack mt="4" spacing="4">
                      <Skeleton height="20px" width="80px" borderRadius="md" />
                      <Skeleton height="20px" width="80px" borderRadius="md" />
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}

            {isErrorPosts && (
              <Alert status="error" borderRadius="xl" mt={0}>
                <AlertIcon />
                <Text>
                  Error fetching posts:{" "}
                  {postsError?.message || "An unknown error occurred."}
                </Text>
              </Alert>
            )}

            {!isLoadingPosts &&
              !isErrorPosts &&
              visiblePosts &&
              visiblePosts.length === 0 && (
                <Flex
                  flexGrow={1}
                  border="2px dashed"
                  borderColor="gray.300"
                  borderRadius="xl"
                  p={10}
                  justifyContent="center"
                  alignItems="center"
                  minHeight="300px"
                  direction="column"
                  gap={4}
                  mt={0}
                  bg="whiteAlpha.500"
                >
                  <Text color="gray.500" fontSize="lg">
                    No experiences shared yet.
                  </Text>
                  <Text color="gray.400" fontSize="sm">
                    Be the first to share yours using the input bar below!
                  </Text>
                </Flex>
              )}

            {!isLoadingPosts &&
              !isErrorPosts &&
              visiblePosts &&
              visiblePosts.length > 0 && (
                <VStack spacing={0} align="stretch" mt={0}>
                  {visiblePosts.map((post) => (
                    <PostCard
                      key={post._id}
                      post={post}
                      currentDoctorId={doctorInfo?._id}
                      onNavigateToDetail={handlePrepareToNavigateToDetail}
                    />
                  ))}
                </VStack>
              )}
          </VStack>
        </Box>

        {showScrollActionButtons && (
          <Box
            position="fixed"
            bottom={{ base: "calc(70px + 64px + 30px + 20px)", md: "120px" }}
            right={{ base: "15px", md: "30px" }}
            zIndex={150}
          >
            <VStack spacing={3}>
              <IconButton
                aria-label="Scroll back down"
                icon={<ArrowDownIcon />}
                onClick={handleScrollBackDown}
                isRound
                size="lg"
                colorScheme="blue"
                boxShadow="xl"
              />
              <IconButton
                aria-label="Refresh posts"
                icon={<RepeatIcon />}
                onClick={handleRefreshPosts}
                isRound
                size="lg"
                colorScheme="green"
                boxShadow="xl"
              />
            </VStack>
          </Box>
        )}
      </Box>

      <Flex
        as="footer"
        position="fixed"
        bottom="0"
        left={{ base: "0", md: "var(--current-sidebar-width)" }}
        width={{
          base: "100%",
          md: "calc(100% - var(--current-sidebar-width))",
        }}
        alignItems="flex-end"
        justifyContent="space-between"
        p={{ base: 2, md: 3 }}
        zIndex={100}
        gap={2}
      >
        <Flex
          direction="column"
          flexGrow={1}
          bg="white"
          boxShadow="xl"
          p={3}
          borderRadius="xl"
          overflow="hidden"
        >
          {submissionStatus === "reviewing" ? (
            <>
              <Flex
                mb={4}
                p={4}
                borderRadius="xl"
                bg="gray.100"
                opacity={0.7}
                filter="blur(0.5px)"
                boxShadow="sm"
                justify="space-between"
                align="flex-start"
                gap={4}
                width="100%"
                position="relative"
                cursor="pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReturnToEdit();
                }}
                _hover={{ opacity: 1, filter: "none", bg: "gray.200" }}
                transition="all 0.2s"
              >
                <IconButton
                  aria-label="Back to edit"
                  icon={
                    <ChevronDownIcon style={{ transform: "rotate(90deg)" }} />
                  }
                  size="sm"
                  variant="ghost"
                  position="absolute"
                  top={2}
                  left={2}
                  zIndex={2}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReturnToEdit();
                  }}
                  isRound
                  _hover={{ bg: "gray.200", opacity: 1, filter: "none" }}
                />
                <Box flex="1" minW="0" pl="32px">
                  <Text
                    fontSize="sm"
                    color="gray.700"
                    mb={2}
                    noOfLines={4}
                    wordBreak="break-word"
                  >
                    {newPostText}
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    {selectedTags.map((tag) => (
                      <Box
                        key={tag}
                        bg="blue.100"
                        color="blue.800"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        fontSize="xs"
                      >
                        {tag}
                      </Box>
                    ))}
                  </Flex>
                </Box>
                <Flex gap={2} flexWrap="wrap">
                  {previewUrls.map((url, idx) => {
                    const file = selectedFiles[idx];
                    if (!file) return null;
                    if (file.type.startsWith("image/")) {
                      return (
                        <Image
                          key={url + idx + "-summary"}
                          src={url}
                          alt={`Summary preview ${idx + 1}`}
                          boxSize="56px"
                          objectFit="cover"
                          borderRadius="lg"
                          opacity={0.8}
                        />
                      );
                    }
                    if (file.type.startsWith("video/")) {
                      return (
                        <Box
                          key={url + idx + "-summary-video"}
                          borderRadius="lg"
                          overflow="hidden"
                          w="56px"
                          h="56px"
                          bg="black"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <video
                            src={url}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              opacity: 0.8,
                            }}
                          />
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Flex>
              </Flex>
              <Box mb={4}>
                <Text fontSize="xs" color="gray.500" mb={1} textAlign="left">
                  We generated a Title for you ✨
                </Text>
                {isGeneratingGeminiTitle ? (
                  <>
                    <Skeleton
                      height="36px"
                      width="80%"
                      marginX="auto"
                      my={4}
                      borderRadius="md"
                      speed={0.8}
                    />
                    <SkeletonText
                      mt="2"
                      mb="3"
                      noOfLines={1}
                      spacing="3"
                      skeletonHeight="4"
                      width="70%"
                      marginX="auto"
                      speed={0.8}
                    />
                  </>
                ) : isEditingTitleManually ? (
                  <VStack spacing={2} align="stretch" my={2}>
                    <Input
                      value={postTitle}
                      onChange={(e) => {
                        setPostTitle(e.target.value);
                        if (reason) setReason("");
                        if (typingStage !== "finished")
                          setTypingStage("finished");
                        if (displayedTitle !== e.target.value)
                          setDisplayedTitle(e.target.value);
                        if (displayedReason) setDisplayedReason("");
                      }}
                      placeholder="Enter your title"
                      size="md"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      variant="link"
                      colorScheme="blue"
                      onClick={() => setIsEditingTitleManually(false)}
                    >
                      Use AI Generated Title
                    </Button>
                  </VStack>
                ) : (
                  <>
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      my={4}
                      minHeight="36px"
                    >
                      <Text
                        fontSize="xl"
                        fontWeight="bold"
                        textAlign="center"
                        flex="1"
                        ml={8}
                      >
                        {displayedTitle}
                        {(typingStage === "title" ||
                          typingStage === "reason") && (
                          <Box as="span" animation="blink 1s infinite">
                            |
                          </Box>
                        )}
                      </Text>
                      <IconButton
                        aria-label="Edit title manually"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsEditingTitleManually(true);
                          if (
                            typingStage === "title" ||
                            typingStage === "reason"
                          ) {
                            setDisplayedTitle(postTitle);
                          }
                          if (typingStage !== "finished")
                            setTypingStage("finished");
                        }}
                        isRound
                      />
                    </Flex>
                    {(displayedReason ||
                      (typingStage === "reason" && reason)) && (
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        mt={0}
                        mb={3}
                        textAlign="center"
                        minHeight="20px"
                      >
                        {displayedReason}
                        {typingStage === "reason" && (
                          <Box as="span" animation="blink 1s infinite">
                            |
                          </Box>
                        )}
                      </Text>
                    )}
                  </>
                )}
                <Flex alignItems="center" gap={2} justifyContent="center">
                  <IconButton
                    aria-label="Previous title"
                    icon={
                      <ChevronDownIcon style={{ transform: "rotate(90deg)" }} />
                    }
                    size="sm"
                    variant="ghost"
                    onClick={handlePrevTitle}
                    isDisabled={
                      isEditingTitleManually ||
                      titleHistoryIndex <= 0 ||
                      isGeneratingGeminiTitle ||
                      typingStage === "title" ||
                      typingStage === "reason"
                    }
                    isRound
                  />
                  <Button
                    colorScheme="blue"
                    variant="ghost"
                    size="sm"
                    onClick={handleChangeTitle}
                    isLoading={isGeneratingGeminiTitle}
                    isDisabled={
                      isGeneratingGeminiTitle ||
                      typingStage === "title" ||
                      typingStage === "reason"
                    }
                    borderRadius="xl"
                  >
                    Change
                  </Button>
                  <IconButton
                    aria-label="Next title"
                    icon={
                      <ChevronDownIcon
                        style={{ transform: "rotate(-90deg)" }}
                      />
                    }
                    size="sm"
                    variant="ghost"
                    onClick={handleNextTitle}
                    isDisabled={
                      isEditingTitleManually ||
                      titleHistoryIndex >= titleHistory.length - 1 ||
                      isGeneratingGeminiTitle ||
                      typingStage === "title" ||
                      typingStage === "reason"
                    }
                    isRound
                  />
                </Flex>
              </Box>
              <Flex alignItems="center" width="100%">
                <Box
                  flexGrow={1}
                  height="40px"
                  bg="gray.400"
                  borderRadius="full"
                  overflow="hidden"
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    bg="blue.500"
                    height="100%"
                    width={`${fillPercentage}%`}
                    transition="width 1s linear, background-color 0.3s ease"
                    position="absolute"
                    left="0"
                    top="0"
                    _hover={{ bg: "blue.600" }}
                  />
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color="white"
                    position="absolute"
                    left="50%"
                    top="50%"
                    transform="translate(-50%, -50%)"
                    zIndex="1"
                    pointerEvents="none"
                  >
                    Submitting in {remainingTime} seconds
                  </Text>
                </Box>
                {(() => {
                  const canStartOrResumeTimer =
                    submissionStatus === "reviewing" &&
                    aiResultDisplayed &&
                    fillPercentage < 100 &&
                    !createExperienceMutation.isPending;
                  const isTimerCurrentlyRunning = isFilling && !isTimerPaused;
                  return (
                    <IconButton
                      aria-label={
                        isTimerCurrentlyRunning
                          ? "Pause submission timer"
                          : canStartOrResumeTimer
                          ? "Resume submission timer"
                          : "Timer control"
                      }
                      icon={
                        isTimerCurrentlyRunning ? <PauseIcon /> : <PlayIcon />
                      }
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (isTimerCurrentlyRunning) {
                          setIsTimerPaused(true);
                        } else if (canStartOrResumeTimer) {
                          setIsTimerPaused(false);
                          setIsFilling(true);
                        }
                      }}
                      ml={2}
                      isDisabled={
                        !canStartOrResumeTimer && !isTimerCurrentlyRunning
                      }
                      isRound
                    />
                  );
                })()}
              </Flex>
            </>
          ) : (
            <>
              {submissionStatus !== "pending" && (
                <Box position="relative">
                  {showTagsInput && (
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      mb={3}
                    >
                      <Heading size="sm" textAlign="left">
                        Add your Experience
                      </Heading>
                      <IconButton
                        aria-label="Collapse input area"
                        icon={
                          <ChevronDownIcon
                            style={{
                              transform: showTagsInput
                                ? "rotate(0deg)"
                                : "rotate(180deg)",
                            }}
                          />
                        }
                        size="sm"
                        variant="ghost"
                        onClick={handleCollapseInput}
                        isRound
                      />
                    </Flex>
                  )}
                  <Textarea
                    placeholder={
                      submissionStatus === "submitted"
                        ? "Start typing for a new post..."
                        : "Add your experience here..."
                    }
                    value={newPostText}
                    onChange={handleNewPostTextChange}
                    size="sm"
                    borderRadius="xl"
                    minH={showTagsInput ? "100px" : "40px"}
                    maxH="150px"
                    resize="none"
                    transition="min-height 0.2s ease-in-out, background-color 0.2s ease-in-out"
                    isReadOnly={false}
                    pr={showTagsInput ? "40px" : "8px"}
                    bg={showTagsInput ? "rgba(144, 238, 144, 0.1)" : "white"}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                    }}
                  />
                </Box>
              )}
              <Collapse in={showTagsInput} animateOpacity>
                {submissionStatus === "pending" ? (
                  <Box mt={3} py={3} px={2}>
                    {" "}
                  </Box>
                ) : submissionStatus === "submitted" ? (
                  <Box mt={3} py={4} textAlign="center">
                    {" "}
                    <Text fontStyle="italic" color="gray.500">
                      Your post has been submitted successfully.
                    </Text>{" "}
                    <Text fontSize="sm" color="gray.400" mt={1}>
                      Start typing above or attach files to add another.
                    </Text>{" "}
                  </Box>
                ) : (
                  <Box mt={3}>
                    {previewUrls.length > 0 && (
                      <Flex
                        width="100%"
                        mb={3}
                        p={1}
                        overflowX="auto"
                        gap={2}
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="xl"
                      >
                        {previewUrls.map((url, index) => {
                          const file = selectedFiles[index];
                          const isBroken = brokenImageUrls.has(url);
                          if (!file) return null;
                          return (
                            <Box
                              key={url + index + "-mainpreview"}
                              p={0.5}
                              h="100px"
                              w="100px"
                              minW="100px"
                              overflow="hidden"
                              position="relative"
                              borderWidth="1px"
                              borderColor="gray.300"
                              borderRadius="lg"
                              cursor={file && !isBroken ? "pointer" : "default"}
                              onClick={() =>
                                file &&
                                !isBroken &&
                                (file.type.startsWith("image/") ||
                                  file.type.startsWith("video/")) &&
                                handlePreviewClick(
                                  url,
                                  file.type.startsWith("image/")
                                    ? "image"
                                    : "video"
                                )
                              }
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (
                                  file &&
                                  !isBroken &&
                                  (file.type.startsWith("image/") ||
                                    file.type.startsWith("video/")) &&
                                  (e.key === "Enter" || e.key === " ")
                                ) {
                                  handlePreviewClick(
                                    url,
                                    file.type.startsWith("image/")
                                      ? "image"
                                      : "video"
                                  );
                                }
                              }}
                              _hover={{ borderColor: "blue.500" }}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {" "}
                              {file.type.startsWith("image/") ? (
                                isBroken ? (
                                  <Text
                                    fontSize="xs"
                                    color="red.500"
                                    textAlign="center"
                                  >
                                    Preview unavailable
                                  </Text>
                                ) : (
                                  <Image
                                    src={url}
                                    alt={`Preview ${index + 1}`}
                                    h="100%"
                                    w="100%"
                                    objectFit="cover"
                                    onError={() => handlePreviewImageError(url)}
                                  />
                                )
                              ) : null}{" "}
                              {file.type.startsWith("video/") && (
                                <>
                                  <video
                                    src={url}
                                    style={{
                                      height: "100%",
                                      width: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <Flex
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    alignItems="center"
                                    justifyContent="center"
                                    bg="rgba(0,0,0,0.3)"
                                    pointerEvents="none"
                                  >
                                    <Icon
                                      as={PlayIcon}
                                      w={8}
                                      h={8}
                                      color="white"
                                    />
                                  </Flex>
                                </>
                              )}{" "}
                              <IconButton
                                aria-label="Remove this file"
                                icon={<CloseIcon boxSize="8px" />}
                                size="xs"
                                isRound
                                position="absolute"
                                top="4px"
                                right="4px"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveSingleFile(index);
                                }}
                                bg="blackAlpha.500"
                                color="white"
                                _hover={{ bg: "blackAlpha.700" }}
                                zIndex="1"
                                boxShadow="xs"
                                minW="auto"
                                w="20px"
                                h="20px"
                              />
                            </Box>
                          );
                        })}
                      </Flex>
                    )}
                    <Button
                      leftIcon={<AttachmentIcon />}
                      variant="outline"
                      size="sm"
                      onClick={handleAttachmentClick}
                      isDisabled={selectedFiles.length >= MAX_FILES}
                      title={
                        selectedFiles.length >= MAX_FILES
                          ? `Maximum ${MAX_FILES} files allowed`
                          : "Add attachment"
                      }
                      mb={3}
                      width="100%"
                      borderRadius="xl"
                      borderColor="gray.300"
                    >
                      Add Photos/Videos{" "}
                      {selectedFiles.length > 0
                        ? `(${selectedFiles.length}/${MAX_FILES})`
                        : ""}
                    </Button>
                    <Box width="100%" mb={3} position="relative">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        Tags* (comma-separated)
                      </Text>
                      <Flex wrap="wrap" gap={2} mb={1} alignItems="center">
                        {selectedTags.map((tag) => (
                          <Box
                            key={tag}
                            bg="blue.100"
                            px={2}
                            py={1}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                          >
                            <Text fontSize="sm" color="blue.800">
                              {tag}
                            </Text>
                            <IconButton
                              aria-label="Remove tag"
                              icon={<CloseIcon boxSize="8px" />}
                              size="xs"
                              ml={1.5}
                              onClick={() => handleRemoveTag(tag)}
                              variant="ghost"
                              isRound
                              minW="auto"
                              h="auto"
                              p="1px"
                              color="blue.600"
                              _hover={{ bg: "blue.200" }}
                            />
                          </Box>
                        ))}{" "}
                        <Input
                          placeholder="Type and use comma to add"
                          value={tagInput}
                          onChange={handleTagInputChange}
                          size="sm"
                          borderRadius="xl"
                          focusBorderColor={"blue.500"}
                          isDisabled={false}
                          width="auto"
                          flex="1"
                          minW="120px"
                        />
                      </Flex>
                      {tagSuggestions.length > 0 && (
                        <Box
                          position="absolute"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="xl"
                          mt={1}
                          zIndex={1000}
                          width="100%"
                          boxShadow="lg"
                        >
                          {tagSuggestions.map((tag) => (
                            <Box
                              key={tag}
                              px={3}
                              py={2}
                              cursor="pointer"
                              _hover={{ bg: "blue.50" }}
                              onClick={() => handleTagSuggestionClick(tag)}
                            >
                              {tag}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Button
                      rightIcon={<ArrowUpIcon />}
                      colorScheme="blue"
                      variant="solid"
                      size="sm"
                      onClick={handlePreparePostForReview}
                      isLoading={isPreparingReview}
                      isDisabled={
                        (!newPostText.trim() && selectedFiles.length === 0) ||
                        selectedTags.length === 0 ||
                        isPreparingReview
                      }
                      width="100%"
                      borderRadius="xl"
                    >
                      Add Yours
                    </Button>
                  </Box>
                )}
              </Collapse>
            </>
          )}
          <Input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: "none" }}
            id="file-upload-input-meet"
          />
        </Flex>
        <Box position="relative">
          <IconButton
            aria-label="Toggle Messages Pane"
            icon={<ChatIcon />}
            onClick={handleToggleMessages}
            bg={"blue.600"}
            color="white"
            h="64px"
            w="64px"
            borderRadius="xl"
            isRound={false}
            boxShadow="lg"
          />
          {true && (
            <Box
              as="span"
              position="absolute"
              top="-1px"
              right="-1px"
              px={1.5}
              py={0.5}
              fontSize="xs"
              fontWeight="bold"
              lineHeight="none"
              color="white"
              bg="red.500"
              borderRadius="full"
              minW="10px"
              h="10px"
            ></Box>
          )}
        </Box>
      </Flex>
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent borderRadius="xl">
          <ModalHeader textTransform="capitalize">
            {modalContent?.type} Preview
          </ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody display="flex" justifyContent="center" alignItems="center">
            {modalContent?.type === "image" && modalContent?.url && (
              <Image
                src={modalContent.url}
                alt="Enlarged preview"
                maxH="80vh"
                maxW="100%"
                objectFit="contain"
                borderRadius="lg"
              />
            )}
            {modalContent?.type === "video" && modalContent?.url && (
              <video
                src={modalContent.url}
                controls
                autoPlay
                style={{
                  maxHeight: "80vh",
                  maxWidth: "100%",
                  borderRadius: "0.5rem",
                }}
              >
                Your browser does not support the video tag.
              </video>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onModalClose} borderRadius="xl">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Meet;
